import { PenTestVulnerability } from 'it/primatoo/openapi/api/org/schemas/cybersecurity/PenTestVulnerability';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Vulnerability } from 'it/primatoo/openapi/api/org/schemas/cybersecurity/_apppentest/Vulnerability';
import { ColDef, ILargeTextEditorParams } from 'ag-grid-community';
import {
  AgGridTableWrapper,
  CheckboxRendererComponent,
  CustomRendererComponent,
  SelectCellEditableRendererComponent,
  TextCellEditableRendererComponent
} from '@pt/pt-shared';
import { PenTestSeverity } from 'it/primatoo/openapi/api/org/schemas/cybersecurity/PenTestSeverity';
import { ColumnWithLocalization } from 'it/primatoo/openapi/api/org/schemas/common/ColumnWithLocalization';
import { TemplateRef } from '@angular/core';
import { UniquePenTestVulnerability } from './pen-test-dialog.model';


export function PenVulnerabilityValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const vulnerabilities: PenTestVulnerability[] = control.value;
    if (!vulnerabilities) {
      return null;
    }
    for (const vulnerability of vulnerabilities) {
      switch (true) {
        case !vulnerability.uuid || vulnerability.uuid.length === 0:
          return {
            vulnerability: {
              value: 'Missing uuid'
            }
          };
        case !vulnerability.name || vulnerability.name.length === 0:
          return {
            vulnerability: {
              value: 'Missing name'
            }
          };
        case !vulnerability.severity || vulnerability.severity.length === 0:
          return {
            vulnerability: {
              value: 'Missing severity'
            }
          };

        case !Number.isSafeInteger(vulnerability.confidentialityViolation): // || vulnerability.confidentialityViolation === 0:
          return {
            vulnerability: {
              value: 'Missing confidentialityViolation'
            }
          };
        case !Number.isSafeInteger(vulnerability.integrityCompromise): // || vulnerability.integrityCompromise === 0:
          return {
            vulnerability: {
              value: 'Missing integrityCompromise'
            }
          };
        case !Number.isSafeInteger(vulnerability.availabilityLoss): // || vulnerability.availabilityLoss === 0:
          return {
            vulnerability: {
              value: 'Missing availabilityLoss'
            }
          };
      }
    }
    return null;
  };
}

export class PenTestUtils {
  static VALID_COLOR = '#00ff8961';
  static INVALID_COLOR = '#ff18007a';
  static threatEvalLabels(): string[] {
    return [
      // "-",
      "Nullo",
      "Basso",
      "Alto"
    ]
  }

  static severityLabels(): string[] {
    return [
      "Nessuno",
      "Basso",
      "Medio",
      "Alto",
      "Critico"
    ]
  }

  static vulnerabilities2uniquePTVulnerabilityList(items: Vulnerability[]): UniquePenTestVulnerability[] {
    const response: UniquePenTestVulnerability[] = items.map((item, index) => {
      return PenTestUtils.vulnerability2uniquePTVulnerability(item, index + 1)
    });
    return response;
  }

  static vulnerability2uniquePTVulnerability(vulnerability: Vulnerability, rowUniqueId: number): UniquePenTestVulnerability {
    const response: UniquePenTestVulnerability = {
      rowUniqueId: rowUniqueId,
      uuid: vulnerability.uuid,
      name: vulnerability.uuid,
      severity: vulnerability.severity,
      description: vulnerability.description,
      remedy: vulnerability.remedy,
      resolved: vulnerability.resolved,
      confidentialityViolation: vulnerability.confidentialityViolation,
      integrityCompromise: vulnerability.integrityCompromise,
      availabilityLoss: vulnerability.availabilityLoss
    }
    return response;
  }

  static uniquePTVulnerabilityList2PTVulnerabilityList(items: UniquePenTestVulnerability[]): PenTestVulnerability[] {
    const response: PenTestVulnerability[] = items.map(item => {
      return PenTestUtils.uniquePTVulnerabilityList2PTVulnerability(item)
    });
    return response;
  }

  static uniquePTVulnerabilityList2PTVulnerability(uniquePenTestVulnerability: UniquePenTestVulnerability): PenTestVulnerability {
    const response: PenTestVulnerability = {
      uuid: uniquePenTestVulnerability.uuid,
      name: uniquePenTestVulnerability.name,
      severity: uniquePenTestVulnerability.severity,
      description: uniquePenTestVulnerability.description,
      remedy: uniquePenTestVulnerability.remedy,
      resolved: uniquePenTestVulnerability.resolved,
      confidentialityViolation: uniquePenTestVulnerability.confidentialityViolation,
      integrityCompromise: uniquePenTestVulnerability.integrityCompromise,
      availabilityLoss: uniquePenTestVulnerability.availabilityLoss
    }
    return response;
  }
  public static colDefSeverity(c: ColumnWithLocalization, editable: boolean): ColDef {
    const severityEnums = Object.keys(PenTestSeverity)
    const severityLabels = PenTestUtils.severityLabels();
    const col: ColDef = AgGridTableWrapper.generateColDef(c);
    col.editable = editable;
    col.cellRenderer = SelectCellEditableRendererComponent;
    col.cellRendererParams = {
      formatValue: value => {
        const index = severityEnums.indexOf(value);
        return severityLabels[index]
      },
      showSelect: editable
    };
    col.floatingFilter = true;
    col.filter = 'agTextColumnFilter';
    if (editable) {
      col.cellEditor = 'agRichSelectCellEditor';
      col.cellEditorParams = {
        cellHeight: 20,
      };
      col.cellEditorSelector = params => {
        return {
          component: 'agRichSelectCellEditor',
          cellRenderer: SelectCellEditableRendererComponent,
          params: {
            valueListGap: 0,
            values: severityEnums, //.map((i, index) => index),
            formatValue: value => {
              const index = severityEnums.indexOf(value);
              return severityLabels[index]
            },
          },
          popup: true
        }
      };
    }
    return col;
  }

  public static colDefCustom(templateRef: TemplateRef<unknown>, headerName: string, editable: boolean, minWidth = 100): ColDef {
    const col: ColDef = {
      headerName,
      field: 'agGridActionsCol',
      flex: 1,
      minWidth,
      sortable: true,
      editable,
      resizable: true,
      floatingFilter: true,
      filter: 'agTextColumnFilter',
      cellRenderer: CustomRendererComponent,
      cellRendererParams: {
        ngTemplate: templateRef
      }
    }
    return col;
  }


  public static colDefAction(templateRef: TemplateRef<unknown>, minWidth: number = 50): ColDef {
    const col: ColDef = {
      headerName: '',
      field: 'agGridActionsCol',
      flex: 1,
      width: minWidth,
      minWidth: minWidth,
      pinned: 'right',
      suppressNavigable: true,
      lockVisible: true,
      resizable: false,
      editable: false,
      suppressMovable: true,
      cellClass: 'no-border',
      cellRenderer: CustomRendererComponent,
      cellRendererParams: {
        ngTemplate: templateRef
      }
    }
    return col;
  }

  public static colDefCheckBox(c: ColumnWithLocalization, editable: boolean): ColDef {
    const col: ColDef = AgGridTableWrapper.generateColDef(c);
    col.editable = editable;
    col.cellRenderer = CheckboxRendererComponent;
    return col;
  }

  public static colDefText(c: ColumnWithLocalization, editable: boolean): ColDef {
    const col: ColDef = AgGridTableWrapper.generateColDef(c);
    col.editable = editable;
    col.cellRenderer = TextCellEditableRendererComponent;
    if (editable) {
      col.cellStyle = params => {
        let color = 'transparent';
        const value: string = params.value;
        if (value && value.length > 0) {
        } else {
          color = PenTestUtils.INVALID_COLOR;
        }
        return {
          backgroundColor: color
        };
      };
    }
    return col;
  }

  public static colDefTextArea(c: ColumnWithLocalization, editable: boolean): ColDef {
    const col: ColDef = AgGridTableWrapper.generateColDef(c);
    col.editable = editable;
    col.cellRenderer = TextCellEditableRendererComponent;
    if (editable) {
      col.cellEditorPopup = true;
      col.cellEditor = 'agLargeTextCellEditor';
      col.cellEditorParams = {
        maxLength: 250,
        rows: 10,
        cols: 50,
      } as ILargeTextEditorParams;
    }
    col.flex = 2;
    return col;
  }

  public static colDefThreat(c: ColumnWithLocalization, editable: boolean): ColDef {
    const threatEvalLabels = PenTestUtils.threatEvalLabels();
    const col: ColDef = AgGridTableWrapper.generateColDef(c);
    col.editable = editable;
    col.floatingFilter = true;
    col.filter = 'agTextColumnFilter';
    col.cellRenderer = SelectCellEditableRendererComponent;
    col.cellRendererParams = {
      formatValue: value => {
        return threatEvalLabels[value]
      },
      showSelect: editable
    };
    if (editable) {
      col.cellEditor = 'agRichSelectCellEditor';
      col.cellEditorSelector = () => {
        return {
          component: 'agRichSelectCellEditor',
          cellRenderer: SelectCellEditableRendererComponent,
          params: {
            values: threatEvalLabels.map((i, index) => index),
            formatValue: value => threatEvalLabels[value],
            // nullValue: 0
          },
          popup: false
        }
      };
      col.cellStyle = params => {
        let color = 'transparent';
        if (!PenTestUtils.isValidNumber(params.value)) {
          color = PenTestUtils.INVALID_COLOR;
        }
        return {
          backgroundColor: color
        };
      };
    }

    return col;
  }

  public static isValidNumberAndNotZero(num: any): boolean {
    return  PenTestUtils.isValidNumber(num) && num !== 0;
  }

  public static isValidNumber(num: any): boolean {
    let response = true;
    switch (true) {
      case num === null:
      case num === undefined:
      case !Number.isSafeInteger(num):
        response = false;
        break;
      default:
        response = true;
    }
    return response;
  }
}
